import React, { useCallback, useMemo } from 'react';

import { range } from 'lodash-es';
import { useSelector } from 'react-redux';

import Select, { OptionType, OptionsType, SelectProps } from '@atlaskit/select';
import { MIN_TAB_SIZE, MAX_TAB_SIZE } from '@atlassian/bitkit-diff';

import { useIntl } from 'src/hooks/intl';
import { getIsSiteMessageActive } from 'src/selectors/global-selectors';

import { useDiffSettingsForm } from './diff-settings-form';
import messages from './i18n';
import { useSelectStyles } from './styled';

export const tabSizePrefId = 'tab-size-pref';

export const TabSizeField = React.memo((props: SelectProps<OptionType>) => {
  const intl = useIntl();

  const isSiteBannerOpen = useSelector(getIsSiteMessageActive);

  const [formState, setFormState] = useDiffSettingsForm();

  const handleChange = useCallback(
    (option: OptionType) => {
      const tabSize = Number(option.value);
      setFormState(state => ({ ...state, tabSize }));
    },
    [setFormState]
  );

  const tabSizeOptions: OptionsType = useMemo(
    () =>
      range(MIN_TAB_SIZE, MAX_TAB_SIZE + 1).map(size => ({
        label: intl.formatMessage(messages.tabSizeOptionLabel, {
          tabWidth: size.toString(),
        }),
        value: size.toString(),
      })),
    [intl]
  );

  const noMarginStyles = useSelectStyles(isSiteBannerOpen, 0);

  return (
    <Select
      {...props}
      inputId={tabSizePrefId}
      isDisabled={props.isDisabled}
      isSearchable={false}
      menuPortalTarget={document.body}
      name="tabSize"
      onChange={handleChange}
      options={tabSizeOptions}
      styles={noMarginStyles}
      value={tabSizeOptions.find(
        (option: OptionType) => Number(option.value) === formState.tabSize
      )}
    />
  );
});
